/**
 * @file User / material analysis UI — pages/Dashboard/User/MaterialAnalysisInput/MaterialAnalysisInput.tsx.
 */

import React, { useMemo, useEffect, useState, useCallback } from "react";
import "../../../../styles/formBase.css";
import "./MaterialAnalysisInput.css";
import { useMaterialAnalysisData } from "./hooks/useMaterialAnalysisData";
import { useReliabilityDropdowns } from "./hooks/useReliabilityDropdowns";
import { useStateDropdown } from "./hooks/useStateDropdown";
import { useGeographicScopeDropdown } from "../../../../hooks/useGeographicScopeDropdown";
import {
  resolveGeographicScopeLabel,
} from "../../../../utils/backgroundGeographicalSpecificity";
import { useMaterialAnalysisForm } from "./hooks/useMaterialAnalysisForm";
import { MaterialSelectionCard } from "./components/MaterialSelectionCard";
import { ReliabilityStandardsCard } from "./components/ReliabilityStandardsCard";
import { ResultsSection } from "./components/ResultsSection";
import { DataSpecificitySection } from "./components/DataSpecificitySection";
import { buildMaterialAnalysisGeoParams } from "../../../../utils/backgroundGeographicalSpecificity";
import { EmbodiedCarbonFactorCard } from "./components/EmbodiedCarbonFactorCard";
import { SubmissionsDataTable } from "./components/SubmissionsDataTable";
import { MaterialAnalysisPdfReport } from "./components/MaterialAnalysisPdfReport";
import {
  getMaterialAnalysis,
  MaterialAnalysisResponse,
  MaterialAnalysisParams,
} from "../../../../services/materialAnalysisService";
import {
  reliabilityStandardsService,
  ReliabilityStandardConfig,
} from "../../../../services/reliabilityStandardsService";
import {
  downloadMaterialAnalysisExcel,
  downloadMaterialAnalysisPdf,
} from "./utils/materialAnalysisExport";
import { getVisibleResultTable } from "./utils/getVisibleResultTable";
import type { DropdownOption } from "../../../../interfaces/dropdownInterfaces";
import { AGNOSTIC_SUB_MATERIAL_LABEL } from "./materialAnalysisConstants";
import {
  formatMaterialPropertyRangeLabel,
  getMaterialPropertyByKey,
} from "./materialAnalysisPropertyConfig";

interface MaterialAnalysisInputProps {}

/** Map form geoSpec/geoSelection to API geographicScope, country, state */
function buildGeoParams(geoSpec: string, geoSelection: string) {
  return buildMaterialAnalysisGeoParams(geoSpec, geoSelection);
}

const MaterialAnalysisInput: React.FC<MaterialAnalysisInputProps> = () => {
  const {
    formValues,
    MIN_YEAR,
    MAX_YEAR,
    handleTemporalChange,
    handleGeoChange,
    handleGeoSelectionChange,
    handleMaterialChange,
    handleSubMaterialChange,
    handleMaterialPropertyKeyChange,
    handleMaterialPropertyMinChange,
    handleMaterialPropertyMaxChange,
    handleManufacturerChange,
    handleTechnologyChange,
    handleReliabilityChange,
    resetFilters,
    temporalSummaryLabel,
    geoSummaryLabel,
  } = useMaterialAnalysisForm();

  const {
    materials,
    subMaterials,
    manufacturers,
    technologies,
    subMaterialsLoading,
  } = useMaterialAnalysisData(formValues.materialId);

  const {
    dataReviewOptions,
    dataCompletenessOptions,
    dataAcquisitionOptions,
  } = useReliabilityDropdowns();

  const { stateOptions } = useStateDropdown();
  const { geographicScopeOptions } = useGeographicScopeDropdown();

  const selectedMaterial = useMemo(
    () => materials.find((m) => m.id === formValues.materialId) ?? null,
    [materials, formValues.materialId]
  );

  const materialPropertyConfig = useMemo(
    () => getMaterialPropertyByKey(formValues.materialPropertyKey),
    [formValues.materialPropertyKey]
  );

  const materialPropertyRangeLabel = useMemo(
    () =>
      formatMaterialPropertyRangeLabel(
        materialPropertyConfig,
        formValues.materialPropertyMin,
        formValues.materialPropertyMax
      ),
    [materialPropertyConfig, formValues.materialPropertyMin, formValues.materialPropertyMax]
  );

  const selectedTechnologyName = useMemo(() => {
    if (!formValues.technologyId) return "Agnostic";
    const match = technologies.find((entry) => entry.id === formValues.technologyId);
    return match?.name ?? "Agnostic";
  }, [formValues.technologyId, technologies]);

  const selectedManufacturerName = useMemo(() => {
    if (!formValues.manufacturerId) return "Agnostic";
    const match = manufacturers.find((entry) => entry.id === formValues.manufacturerId);
    return match?.name ?? "Agnostic";
  }, [formValues.manufacturerId, manufacturers]);

  const [apiResponse, setApiResponse] = useState<MaterialAnalysisResponse | null>(null);
  const [reliabilityStandards, setReliabilityStandards] = useState<ReliabilityStandardConfig | null>(null);
  const [dataSpecificityApplied, setDataSpecificityApplied] = useState(false);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [generatingExcel, setGeneratingExcel] = useState(false);
  const pdfRootRef = React.useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (
      subMaterialsLoading ||
      formValues.subMaterialIsAgnostic ||
      !formValues.materialId ||
      !formValues.subMaterialId
    ) {
      return;
    }
    const stillValid = subMaterials.some(
      (sm) => sm.id === formValues.subMaterialId
    );
    if (!stillValid) {
      handleSubMaterialChange("");
    }
  }, [
    subMaterialsLoading,
    formValues.materialId,
    formValues.subMaterialId,
    formValues.subMaterialIsAgnostic,
    subMaterials,
    handleSubMaterialChange,
  ]);

  useEffect(() => {
    const fetchReliabilityStandards = async () => {
      try {
        const response = await reliabilityStandardsService.getActive();
        if (response.success && response.data) {
          setReliabilityStandards(response.data);
          handleReliabilityChange("dataReview", response.data.dataReviewValue);
          handleReliabilityChange("dataCompleteness", response.data.dataCompletenessValue);
          handleReliabilityChange("dataAcquisition", response.data.dataAcquisitionValue);
        }
      } catch (error) {
        console.error("Failed to fetch reliability standards:", error);
      }
    };
    fetchReliabilityStandards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buildParams = useCallback(
    (applyDataSpecificity: boolean): MaterialAnalysisParams => {
      const propertyMin = formValues.materialPropertyMin.trim();
      const propertyMax = formValues.materialPropertyMax.trim();
      const hasPropertySelection = !!formValues.materialPropertyKey;
      const base: MaterialAnalysisParams = {
        materialId: formValues.materialId!,
        subMaterialId: formValues.subMaterialIsAgnostic
          ? undefined
          : formValues.subMaterialId ?? undefined,
        propertyKey: hasPropertySelection ? formValues.materialPropertyKey : undefined,
        propertyMin: hasPropertySelection && propertyMin ? Number(propertyMin) : undefined,
        propertyMax: hasPropertySelection && propertyMax ? Number(propertyMax) : undefined,
        applyDataSpecificity,
      };
      if (!applyDataSpecificity) return base;
      const [startYear, endYear] = formValues.temporalRange;
      const geo = buildGeoParams(formValues.geoSpec, formValues.geoSelection);
      return {
        ...base,
        startYear,
        endYear,
        ...geo,
        technologyId: formValues.technologyId ?? undefined,
        manufacturerId: formValues.manufacturerId ?? undefined,
      };
    },
    [formValues]
  );

  const fetchMaterialAnalysis = useCallback(
    async (applyDataSpecificity: boolean) => {
      if (!formValues.materialId) {
        setApiResponse(null);
        setDataSpecificityApplied(false);
        return;
      }
      setLoadingAnalysis(true);
      try {
        const params = buildParams(applyDataSpecificity);
        const response = await getMaterialAnalysis(params);
        setApiResponse(response.data);
        setDataSpecificityApplied(response.data.applyDataSpecificity);
      } catch (error) {
        console.error("Failed to fetch material analysis:", error);
        setApiResponse(null);
        setDataSpecificityApplied(false);
      } finally {
        setLoadingAnalysis(false);
      }
    },
    [formValues.materialId, buildParams]
  );

  // Re-fetch only when material selection changes — not when specificity sliders change.
  useEffect(() => {
    if (formValues.materialId) {
      fetchMaterialAnalysis(false);
    } else {
      setApiResponse(null);
      setDataSpecificityApplied(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formValues.materialId,
    formValues.subMaterialId,
    formValues.subMaterialIsAgnostic,
    formValues.materialPropertyKey,
    formValues.materialPropertyMin,
    formValues.materialPropertyMax,
  ]);

  const handleApplyFilter = useCallback(() => {
    if (!formValues.materialId) return;
    fetchMaterialAnalysis(true);
  }, [formValues.materialId, fetchMaterialAnalysis]);

  const handleResetFilters = useCallback(() => {
    resetFilters();
    setDataSpecificityApplied(false);
    if (formValues.materialId) {
      fetchMaterialAnalysis(false);
    } else {
      setApiResponse(null);
    }
  }, [formValues.materialId, resetFilters, fetchMaterialAnalysis]);

  const displayPool = useMemo(() => {
    if (!apiResponse) return undefined;
    if (dataSpecificityApplied) {
      return apiResponse.dataSpecificity;
    }
    const reliableCount = apiResponse.reliable?.count ?? 0;
    if (reliableCount > 0) {
      return apiResponse.reliable;
    }
    return apiResponse.materials;
  }, [apiResponse, dataSpecificityApplied]);

  const displaySubmissions = displayPool?.data ?? [];
  const displayEmbodiedCarbonFactor = displayPool?.embodiedCarbonFactor;
  const hasVisualizationData = (displayPool?.count ?? displaySubmissions.length) > 0;
  const materialSelected = !!formValues.materialId;

  const dataSpecificityCount = dataSpecificityApplied
    ? apiResponse?.dataSpecificity?.count ?? 0
    : undefined;

  const visualizationKey = [
    dataSpecificityApplied ? "specific" : "generic",
    dataSpecificityCount ?? "na",
    apiResponse?.reliable?.count ?? 0,
    apiResponse?.materials?.count ?? 0,
    displayEmbodiedCarbonFactor?.redDotValue ?? "na",
  ].join("-");

  const showDataSpecificityFilters =
    (apiResponse?.materials?.count ?? 0) > 0 || dataSpecificityApplied;

  const selectedMaterialName = selectedMaterial?.name || "Material";
  const selectedSubMaterialName = formValues.subMaterialIsAgnostic
    ? AGNOSTIC_SUB_MATERIAL_LABEL
    : subMaterials.find((sm) => sm.id === formValues.subMaterialId)?.name || "";

  const humanizeKey = useCallback((v: string) => {
    return String(v || "")
      .split("_")
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  }, []);

  const optionLabel = useCallback(
    (options: DropdownOption[], value: string) => {
      if (!value) return "-";
      const match = (options || []).find((o) => String(o.value) === String(value));
      return match?.label || humanizeKey(value);
    },
    [humanizeKey]
  );

  const dataReviewLabel = useMemo(
    () => optionLabel(dataReviewOptions, formValues.dataReview),
    [dataReviewOptions, formValues.dataReview, optionLabel]
  );
  const dataCompletenessLabel = useMemo(
    () => optionLabel(dataCompletenessOptions, formValues.dataCompleteness),
    [dataCompletenessOptions, formValues.dataCompleteness, optionLabel]
  );
  const dataAcquisitionLabel = useMemo(
    () => optionLabel(dataAcquisitionOptions, formValues.dataAcquisition),
    [dataAcquisitionOptions, formValues.dataAcquisition, optionLabel]
  );

  const formValuesForDownload = useMemo(() => {
    return {
      ...formValues,
      dataReview: dataReviewLabel,
      dataCompleteness: dataCompletenessLabel,
      dataAcquisition: dataAcquisitionLabel,
    };
  }, [dataAcquisitionLabel, dataCompletenessLabel, dataReviewLabel, formValues]);

  const geoLabel =
    formValues.geoSpec === "global"
      ? resolveGeographicScopeLabel(
          formValues.geoSelection || "global",
          geographicScopeOptions
        )
      : formValues.geoSelection
      ? formValues.geoSelection
      : "Coverage";

  const visibleResultTable = useMemo(
    () =>
      getVisibleResultTable({
        dataSpecificityApplied,
        materialSubmissions: apiResponse?.materials?.data ?? [],
        reliableSubmissions: apiResponse?.reliable?.data ?? [],
        dataSpecificitySubmissions: apiResponse?.dataSpecificity?.data ?? [],
      }),
    [apiResponse, dataSpecificityApplied]
  );

  const visibleTableSubmissions = visibleResultTable?.submissions ?? [];
  const visibleTableTitle = visibleResultTable?.title ?? "Datasets meeting input requirements";

  const handleDownloadPdf = useCallback(async () => {
    if (generatingPdf) return;
    if (!visibleTableSubmissions.length) return;
    if (!pdfRootRef.current) return;

    setGeneratingPdf(true);
    try {
      await downloadMaterialAnalysisPdf({
        pdfRoot: pdfRootRef.current,
        submissions: visibleTableSubmissions,
        selectedMaterialName,
        tableTitle: visibleTableTitle,
      });
    } catch (error) {
      console.error("Failed to generate PDF export:", error);
    } finally {
      setGeneratingPdf(false);
    }
  }, [visibleTableSubmissions, visibleTableTitle, generatingPdf, selectedMaterialName]);

  const handleDownloadExcel = useCallback(async () => {
    if (generatingExcel) return;
    if (!visibleTableSubmissions.length) return;
    if (!pdfRootRef.current) return;

    setGeneratingExcel(true);
    try {
      await downloadMaterialAnalysisExcel({
        pdfRoot: pdfRootRef.current,
        submissions: visibleTableSubmissions,
        formValues: formValuesForDownload,
        geoLabel,
        selectedMaterialName: selectedMaterialName || "-",
        selectedSubMaterialName: selectedSubMaterialName || "-",
        materialPropertyLabel: materialPropertyRangeLabel,
        selectedTechnologyName,
        selectedManufacturerName,
        tableTitle: visibleTableTitle,
        dataReviewOptions,
        dataCompletenessOptions,
        dataAcquisitionOptions,
      });
    } catch (error) {
      console.error("Failed to generate Excel export:", error);
    } finally {
      setGeneratingExcel(false);
    }
  }, [
    visibleTableSubmissions,
    visibleTableTitle,
    formValuesForDownload,
    generatingExcel,
    geoLabel,
    selectedManufacturerName,
    selectedMaterialName,
    selectedSubMaterialName,
    selectedTechnologyName,
    materialPropertyRangeLabel,
    dataReviewOptions,
    dataCompletenessOptions,
    dataAcquisitionOptions,
  ]);

  return (
    <div className="background-form-container analysis-shell">
      <div className="analysis-banner">
        <span className="analysis-banner__title">
          Indian Construction Material Embodied Carbon Database
        </span>
      </div>

      <div className="analysis-main">
        <MaterialSelectionCard
          materials={materials}
          subMaterials={subMaterials}
          materialId={formValues.materialId}
          subMaterialId={formValues.subMaterialId}
          subMaterialIsAgnostic={formValues.subMaterialIsAgnostic}
          materialPropertyKey={formValues.materialPropertyKey}
          materialPropertyConfig={materialPropertyConfig}
          materialPropertyMin={formValues.materialPropertyMin}
          materialPropertyMax={formValues.materialPropertyMax}
          subMaterialsLoading={subMaterialsLoading}
          onMaterialChange={handleMaterialChange}
          onSubMaterialChange={handleSubMaterialChange}
          onMaterialPropertyKeyChange={handleMaterialPropertyKeyChange}
          onMaterialPropertyMinChange={handleMaterialPropertyMinChange}
          onMaterialPropertyMaxChange={handleMaterialPropertyMaxChange}
        />

        <ReliabilityStandardsCard
          dataReviewOptions={dataReviewOptions}
          dataCompletenessOptions={dataCompletenessOptions}
          dataAcquisitionOptions={dataAcquisitionOptions}
          dataReview={formValues.dataReview}
          dataCompleteness={formValues.dataCompleteness}
          dataAcquisition={formValues.dataAcquisition}
          onDataReviewChange={(value) => handleReliabilityChange("dataReview", value)}
          onDataCompletenessChange={(value) => handleReliabilityChange("dataCompleteness", value)}
          onDataAcquisitionChange={(value) => handleReliabilityChange("dataAcquisition", value)}
          disabled={true}
        />
      </div>

      <ResultsSection
        key={`pool-size-${visualizationKey}`}
        totalSubmissions={apiResponse?.materials?.count ?? 0}
        reliableCount={apiResponse?.reliable?.count ?? 0}
        dataSpecificityCount={dataSpecificityCount}
        dataSpecificityApplied={dataSpecificityApplied}
        hasData={hasVisualizationData}
        materialSelected={materialSelected}
      />

      <div className="analysis-sections-row">
        <EmbodiedCarbonFactorCard
          key={`ecf-${visualizationKey}`}
          hasData={hasVisualizationData}
          materialSelected={materialSelected}
          submissions={displaySubmissions}
          embodiedCarbonFactor={displayEmbodiedCarbonFactor}
          dataSpecificityApplied={dataSpecificityApplied}
          materialLabel={
            formValues.subMaterialIsAgnostic || formValues.subMaterialId
              ? `${materials.find((m) => m.id === formValues.materialId)?.name || "Material"} · ${
                  formValues.subMaterialIsAgnostic
                    ? AGNOSTIC_SUB_MATERIAL_LABEL
                    : subMaterials.find((sm) => sm.id === formValues.subMaterialId)?.name ||
                      "Sub-material"
                }`
              : materials.find((m) => m.id === formValues.materialId)?.name || "Material"
          }
          materialName={materials.find((m) => m.id === formValues.materialId)?.name || "Material"}
          subMaterialName={
            formValues.subMaterialIsAgnostic
              ? AGNOSTIC_SUB_MATERIAL_LABEL
              : formValues.subMaterialId
              ? subMaterials.find((sm) => sm.id === formValues.subMaterialId)?.name
              : undefined
          }
        />

        <DataSpecificitySection
          formValues={formValues}
          technologies={technologies}
          manufacturers={manufacturers}
          stateOptions={stateOptions}
          geographicScopeOptions={geographicScopeOptions}
          MIN_YEAR={MIN_YEAR}
          MAX_YEAR={MAX_YEAR}
          temporalSummaryLabel={temporalSummaryLabel}
          geoSummaryLabel={geoSummaryLabel}
          selectedTechnologyName={selectedTechnologyName}
          onTemporalChange={handleTemporalChange}
          onGeoChange={handleGeoChange}
          onGeoSelectionChange={handleGeoSelectionChange}
          onTechnologyChange={handleTechnologyChange}
          onManufacturerChange={handleManufacturerChange}
          onApplyFilter={handleApplyFilter}
          onResetFilters={handleResetFilters}
          hasData={!!showDataSpecificityFilters}
          materialSelected={materialSelected}
        />
      </div>

      <SubmissionsDataTable
        materialSubmissions={apiResponse?.materials?.data ?? []}
        reliableSubmissions={apiResponse?.reliable?.data ?? []}
        dataSpecificitySubmissions={apiResponse?.dataSpecificity?.data ?? []}
        dataSpecificityApplied={dataSpecificityApplied}
        loading={loadingAnalysis}
        hasData={hasVisualizationData}
        materialSelected={materialSelected}
        displaySubmissions={visibleTableSubmissions}
        onDownloadPdf={handleDownloadPdf}
        downloadingPdf={generatingPdf}
        onDownloadExcel={handleDownloadExcel}
        downloadingExcel={generatingExcel}
        dataReviewOptions={dataReviewOptions}
        dataCompletenessOptions={dataCompletenessOptions}
        dataAcquisitionOptions={dataAcquisitionOptions}
      />

      <div className="pdf-report-root" ref={pdfRootRef} aria-hidden="true">
        <MaterialAnalysisPdfReport
          formValues={formValuesForDownload}
          submissions={visibleTableSubmissions}
          tableTitle={visibleTableTitle}
          embodiedCarbonFactor={displayEmbodiedCarbonFactor}
          dataSpecificityApplied={dataSpecificityApplied}
          materialName={selectedMaterialName}
          subMaterialName={selectedSubMaterialName || undefined}
          materialPropertyLabel={materialPropertyRangeLabel}
          geoLabel={geoLabel}
          technologyName={selectedTechnologyName}
          manufacturerName={selectedManufacturerName}
          dataReviewOptions={dataReviewOptions}
          dataCompletenessOptions={dataCompletenessOptions}
          dataAcquisitionOptions={dataAcquisitionOptions}
        />
      </div>
    </div>
  );
};

/** React component — Material Analysis Input. */
export default MaterialAnalysisInput;
